import React, { useEffect, useRef, useState, useCallback } from "react";
import { FiX, FiDownload, FiTrash2, FiPlay, FiPause, FiWifi, FiWifiOff } from "react-icons/fi";
import api from "../../services/api";
import authService from "../../services/authService";
import {
  Overlay,
  TerminalWindow,
  TerminalHeader,
  TerminalBody,
  TerminalFooter,
  LineRow,
  IconBtn,
  StatusDot,
} from "./styles";

const WS_URL = process.env.REACT_APP_WS_URL || "wss://log.vidal-app.com/ws";
const MAX_BUFFER_LINES = 5000;

/**
 * Terminal flutuante com logs do worker em tempo real.
 *
 * - Carrega tail histórico via REST: GET /projects/:id/worker-logs?tail=500
 * - Recebe novas linhas via WebSocket (type: 'worker_log')
 * - Auto-scroll inteligente (pausa se usuário scrollou pra cima)
 */
export default function WorkerLogTerminal({ projectId, projectName, onClose }) {
  const [lines, setLines] = useState([]); // [{ts, level, line, taskType, operationId}]
  const [wsConnected, setWsConnected] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const bodyRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const userScrolledRef = useRef(false);

  const appendLines = useCallback((newLines) => {
    if (!newLines || newLines.length === 0) return;
    setLines((prev) => {
      const merged = [...prev, ...newLines];
      if (merged.length > MAX_BUFFER_LINES) {
        return merged.slice(-MAX_BUFFER_LINES);
      }
      return merged;
    });
  }, []);

  // Carga inicial (tail histórico)
  const loadHistory = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/projects/${projectId}/worker-logs`, {
        params: { tail: 500 },
      });
      const histLines = (data.lines || []).map((l) => ({
        line: l,
        level: detectLevel(l),
        historical: true,
      }));
      setLines(histLines);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Falha ao carregar logs");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Conexão WebSocket
  useEffect(() => {
    if (!projectId) return;

    const connect = () => {
      const token = authService.getToken();
      if (!token) return;

      const ws = new WebSocket(`${WS_URL}?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setWsConnected(true);
        ws.send(
          JSON.stringify({
            action: "subscribe",
            type: "worker_log",
            projectId,
          })
        );
      };

      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          if (!parsed.success) return;
          const msg = parsed.message;
          if (!msg || msg.type !== "worker_log" || msg.projectId !== projectId) return;
          const payload = msg.data || {};
          const incoming = payload.lines || [];
          if (incoming.length > 0) {
            appendLines(
              incoming.map((entry) => ({
                line: entry.line || "",
                level: entry.level || "info",
                ts: entry.ts,
                taskType: entry.taskType,
                operationId: entry.operationId,
              }))
            );
          }
        } catch (e) {
          // ignora parse errors
        }
      };

      ws.onerror = () => setWsConnected(false);
      ws.onclose = () => {
        setWsConnected(false);
        // reconectar com backoff
        if (!reconnectTimerRef.current) {
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            connect();
          }, 3000);
        }
      };
    };

    loadHistory().then(() => connect());

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (e) {}
        wsRef.current = null;
      }
    };
  }, [projectId, appendLines, loadHistory]);

  // Auto-scroll
  useEffect(() => {
    if (!autoScroll || userScrolledRef.current) return;
    const body = bodyRef.current;
    if (body) {
      body.scrollTop = body.scrollHeight;
    }
  }, [lines, autoScroll]);

  const handleScroll = useCallback(() => {
    const body = bodyRef.current;
    if (!body) return;
    const atBottom = body.scrollHeight - body.scrollTop - body.clientHeight < 40;
    userScrolledRef.current = !atBottom;
  }, []);

  const handleClear = async () => {
    if (!window.confirm("Limpar o log do worker deste projeto? (não interrompe tarefas em execução)")) return;
    try {
      await api.delete(`/projects/${projectId}/worker-logs`);
      setLines([]);
    } catch (err) {
      alert("Erro ao limpar: " + (err.response?.data?.error || err.message));
    }
  };

  const handleDownload = () => {
    const blob = new Blob([lines.map((l) => l.line || "").join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `worker-${projectId}-${Date.now()}.log`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Overlay onMouseDown={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}>
      <TerminalWindow onClick={(e) => e.stopPropagation()}>
        <TerminalHeader>
          <div className="left">
            <StatusDot $connected={wsConnected} />
            <span className="title">Logs do Worker</span>
            {projectName && <span className="subtitle">— {projectName}</span>}
            <span className="counter">{lines.length} linhas</span>
          </div>
          <div className="right">
            <IconBtn
              title={autoScroll ? "Pausar auto-scroll" : "Retomar auto-scroll"}
              onClick={() => {
                setAutoScroll((a) => !a);
                userScrolledRef.current = false;
              }}
            >
              {autoScroll ? <FiPause /> : <FiPlay />}
            </IconBtn>
            <IconBtn title="Baixar log" onClick={handleDownload}>
              <FiDownload />
            </IconBtn>
            <IconBtn title="Limpar log" onClick={handleClear}>
              <FiTrash2 />
            </IconBtn>
            <IconBtn title={wsConnected ? "WebSocket conectado" : "WebSocket desconectado"}>
              {wsConnected ? <FiWifi /> : <FiWifiOff />}
            </IconBtn>
            <IconBtn title="Fechar" onClick={onClose}>
              <FiX />
            </IconBtn>
          </div>
        </TerminalHeader>

        <TerminalBody ref={bodyRef} onScroll={handleScroll}>
          {loading && <div className="loading">Carregando histórico…</div>}
          {error && <div className="error-line">⚠ {error}</div>}
          {!loading && lines.length === 0 && (
            <div className="empty">Nenhum log ainda. Inicie um upload para ver atividade aqui.</div>
          )}
          {lines.map((l, idx) => (
            <LineRow key={idx} $level={l.level || "info"}>
              {l.line || ""}
            </LineRow>
          ))}
        </TerminalBody>

        <TerminalFooter>
          <span>Projeto: <code>{projectId}</code></span>
          <span>{autoScroll ? "Auto-scroll ON" : "Auto-scroll OFF"}</span>
        </TerminalFooter>
      </TerminalWindow>
    </Overlay>
  );
}

function detectLevel(line) {
  if (!line) return "info";
  const upper = line.toUpperCase();
  if (upper.includes("[ERROR]") || upper.includes(" ERROR ") || upper.includes("✗") || upper.includes("💥")) return "error";
  if (upper.includes("[WARN]") || upper.includes(" WARN ") || upper.includes("⚠")) return "warn";
  return "info";
}
